import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Paper,
    Typography,
    Button,
    Switch,
    FormControlLabel,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    CircularProgress,
    Alert,
    Stack
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Notifications as NotificationsIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { deleteAccount } from '../services/api';
import api from '../services/api';

function Settings() {
    const navigate = useNavigate();
    const { currentUser, userProfile, logout } = useAuth();
    const { showNotification } = useNotification();

    const [notificationSettings, setNotificationSettings] = useState({
        emailNotifications: true,
        messageNotifications: true
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // アカウント削除関連
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleting, setDeleting] = useState(false);

    // 通知設定を取得
    useEffect(() => {
        const fetchNotificationSettings = async () => {
            try {
                const response = await api.get('/users/notification-settings');
                if (response.data.success) {
                    setNotificationSettings(response.data.notificationSettings);
                }
            } catch (error) {
                console.error('通知設定取得エラー:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchNotificationSettings();
    }, []);

    // 通知設定の更新
    const handleNotificationChange = async (field) => {
        const newSettings = {
            ...notificationSettings,
            [field]: !notificationSettings[field]
        };

        setSaving(true);
        try {
            const response = await api.put('/users/notification-settings', newSettings);
            if (response.data.success) {
                setNotificationSettings(newSettings);
                showNotification('通知設定を更新しました', 'success');
            }
        } catch (error) {
            console.error('通知設定更新エラー:', error);
            showNotification('通知設定の更新に失敗しました', 'error');
        } finally {
            setSaving(false);
        }
    };

    // アカウント削除の確認ダイアログを開く
    const handleOpenDeleteDialog = () => {
        setDeleteDialogOpen(true);
    };

    // アカウント削除の最終確認
    const handleConfirmDelete = () => {
        setDeleteDialogOpen(false);
        setConfirmDeleteDialogOpen(true);
        setDeleteConfirmText('');
    };

    // アカウント削除実行
    const handleDeleteAccount = async () => {
        if (deleteConfirmText !== '削除') {
            showNotification('「削除」と入力してください', 'warning');
            return;
        }

        setDeleting(true);
        try {
            const result = await deleteAccount();
            if (result.success) {
                showNotification('アカウントを削除しました', 'success');
                // ログアウトしてホームページへ
                await logout();
                navigate('/');
            }
        } catch (error) {
            console.error('アカウント削除エラー:', error);
            showNotification(
                error.response?.data?.message || 'アカウント削除に失敗しました',
                'error'
            );
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom>
                設定
            </Typography>

            {/* 通知設定 */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <NotificationsIcon color="primary" />
                    <Typography variant="h6">
                        通知設定
                    </Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <FormControlLabel
                    control={
                        <Switch
                            checked={notificationSettings.emailNotifications}
                            onChange={() => handleNotificationChange('emailNotifications')}
                            disabled={saving}
                        />
                    }
                    label="メール通知を受け取る"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                    未読メッセージがある場合に、毎日メールで通知します
                </Typography>

                <FormControlLabel
                    control={
                        <Switch
                            checked={notificationSettings.messageNotifications}
                            onChange={() => handleNotificationChange('messageNotifications')}
                            disabled={saving}
                        />
                    }
                    label="メッセージ通知を有効にする"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                    新しいメッセージを受け取ったときに通知します
                </Typography>
            </Paper>

            {/* アカウント削除 */}
            <Paper sx={{ p: 3, border: '1px solid #d32f2f' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <WarningIcon color="error" />
                    <Typography variant="h6" color="error">
                        危険な操作
                    </Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Alert severity="warning" sx={{ mb: 2 }}>
                    アカウントを削除すると、すべてのデータが完全に削除され、復元できなくなります。
                </Alert>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    削除されるデータ:
                </Typography>
                <ul style={{ marginTop: 0, paddingLeft: 20, color: '#666' }}>
                    <li>プロフィール情報</li>
                    <li>投稿した物件情報（非アクティブ化）</li>
                    <li>メッセージ履歴</li>
                    <li>お気に入り情報</li>
                </ul>

                <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleOpenDeleteDialog}
                    fullWidth
                    sx={{ mt: 2 }}
                >
                    アカウントを削除
                </Button>
            </Paper>

            {/* 削除確認ダイアログ（第1段階） */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>
                    アカウント削除の確認
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        本当にアカウントを削除しますか？
                        <br /><br />
                        この操作は取り消すことができません。すべてのデータが完全に削除されます。
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        キャンセル
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">
                        削除を続行
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 削除確認ダイアログ（第2段階・最終確認） */}
            <Dialog
                open={confirmDeleteDialogOpen}
                onClose={() => !deleting && setConfirmDeleteDialogOpen(false)}
            >
                <DialogTitle sx={{ color: 'error.main' }}>
                    最終確認
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        アカウントを削除するには、下のフィールドに「<strong>削除</strong>」と入力してください。
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        placeholder="削除"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        disabled={deleting}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDeleteDialogOpen(false)}
                        disabled={deleting}
                    >
                        キャンセル
                    </Button>
                    <Button
                        onClick={handleDeleteAccount}
                        color="error"
                        variant="contained"
                        disabled={deleting || deleteConfirmText !== '削除'}
                        startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                    >
                        {deleting ? '削除中...' : 'アカウントを削除'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default Settings;
